/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import apiClient from '../api/client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface RoleTemplateDto {
  id: string;
  code: string;
  name: string;
  description: string | null;
  system: boolean;
  moduleScope: string;
  hierarchyLevel: number;
  createdAt: string;
  updatedAt: string | null;
  permissions: Record<string, string[]>; // module -> [actions]
}

export interface PermissionDto {
  id: string;
  module: string;
  action: string;
  description: string;
}

export interface CreateRoleTemplateRequest {
  code: string;
  name: string;
  description?: string;
  hierarchyLevel: number;
  permissionIds?: string[];
  moduleScope?: string;
}

export interface UpdateRoleTemplateRequest {
  name?: string;
  description?: string;
  hierarchyLevel?: number;
  permissionIds?: string[];
}

export interface OrganizationSummaryDto {
  id: string;
  name: string;
  subscriptionTier: string;
  status: string;
}

export interface BroadcastResultDto {
  templateId: string;
  templateCode: string;
  totalOrgs: number;
  updatedOrgs: number;
  createdOrgs: number;
  failedOrgs: number;
  errors: string[];
}

// ─── API ────────────────────────────────────────────────────────────────────

const BASE = '/admin/role-templates';

export async function listTemplates(moduleScope?: string): Promise<RoleTemplateDto[]> {
  const res = await apiClient.get(BASE, {
    params: moduleScope ? { moduleScope } : undefined,
  });
  return res.data.data;
}

export async function getTemplate(id: string): Promise<RoleTemplateDto> {
  const res = await apiClient.get(`${BASE}/${id}`);
  return res.data.data;
}

export async function listPermissions(): Promise<PermissionDto[]> {
  const res = await apiClient.get(`${BASE}/permissions`);
  return res.data.data;
}

export async function listBroadcastTargets(): Promise<OrganizationSummaryDto[]> {
  const res = await apiClient.get(`${BASE}/broadcast-targets`);
  return res.data.data;
}

export async function createTemplate(data: CreateRoleTemplateRequest): Promise<RoleTemplateDto> {
  const res = await apiClient.post(BASE, data);
  return res.data.data;
}

export async function updateTemplate(id: string, data: UpdateRoleTemplateRequest): Promise<RoleTemplateDto> {
  const res = await apiClient.put(`${BASE}/${id}`, data);
  return res.data.data;
}

export async function deleteTemplate(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/${id}`);
}

export async function broadcastTemplate(id: string): Promise<BroadcastResultDto> {
  const res = await apiClient.post(`${BASE}/${id}/broadcast`);
  return res.data.data;
}
